import * as React from "react";
import * as moment from "moment-timezone";
import * as _ from "lodash";
import styled, { css } from "styled-components";
import { useRouteMatch } from "react-router-dom";

import { Icon } from "src/library/components/Icon";
import { Text } from "src/library/components/Text";
import { ITerminology } from "src/library/utils/string-manipulations/tokenize-string";
import { Icons } from "src/library/components/Icon/types";
import ScheduleEditForm, { IScheduleFormData } from "src/core/schedules/components/ScheduleForm/ScheduleEditForm";
import { validateSchedule } from "src/core/schedules/utils";
import ScheduleDetailForm from "src/core/schedules/components/ScheduleForm/ScheduleDetailForm";
import { ISchedule } from "src/core/schedules/api/schedules.api";
import { IDrop } from "src/core/drops/api/drops.api";
import {
  IError,
  IListErrorData
} from "src/core/errors/api/errors.api";
import { IOrderStatus } from "src/core/orders/api/orders.api";
import useList from "src/core/lists/hooks/useList";

interface ListScheduleInfoProps {
  isLoading: boolean;
  isScheduleLoading: boolean;
  schedule: ISchedule | null;
  drop: IDrop;
  terminology: ITerminology;
  handleSuspendSchedule: (id: number) => void;
  handleReactivateSchedule: (id: number) => void;
  handleSetSchedule: (form: IScheduleFormData) => void;
  scheduleErrors?: Array<IError<IListErrorData>>;
  orderStatuses: IOrderStatus[];
}

type ScheduleViewSettingType ="detail" | "edit";

const ListScheduleInfo: React.ReactFCWithChildren<ListScheduleInfoProps> = props => {
  const {
    terminology,
    isLoading,
    schedule,
    drop,
    handleSuspendSchedule,
    handleReactivateSchedule,
    handleSetSchedule,
    isScheduleLoading,
    scheduleErrors,
    orderStatuses
  } = props;

  const [ scheduleViewSetting, setScheduleViewSetting ] = React.useState<ScheduleViewSettingType>("detail");
  const handleScheduleViewSetting = (type: ScheduleViewSettingType) => setScheduleViewSetting(type);
  let scheduleForm = <></>;
  const match = useRouteMatch<{ listId: string }>();
  const listId = parseInt(match.params.listId);
  const [ { currentList: list } ] = useList(listId);

  if (list.data) {
    const { error, message } = validateSchedule(list.data, drop, terminology);

    if (error) {
      scheduleForm = (
        <PopOutContainer>
          <ScheduleStatus className="error">
            <Icon
              cursor="default"
              $fillColor="error"
              size={16}
              icon={Icons.ERRORCIRCLE}
            />

            {" "}

            {message}
          </ScheduleStatus>
        </PopOutContainer>
      );
    } else if (schedule?.validating || isScheduleLoading) {
      scheduleForm = (
        <PopOutContainer>
          <ScheduleStatus className="update">
            <Icon
              cursor="default"
              $fillColor="primaryDark"
              size={16}
              icon={Icons.PENDING}
            />

            {" Standing order loading..."}
          </ScheduleStatus>
        </PopOutContainer>
      );
    } else {
      scheduleForm = (
        <PopOutContainer>
          {scheduleViewSetting === "edit" || !schedule ? (
            <ScheduleEditForm
              list={list.data}
              schedule={schedule}
              isScheduleLoading={isScheduleLoading}
              scheduleTerminology={terminology.schedule.singular.text}
              availableDays={drop.groupedSettings.delivery_route &&
                drop.groupedSettings.delivery_route.deliveryRoute &&
                drop.groupedSettings.delivery_route.deliveryRoute.deliveryDays}
              handleClose={() => setScheduleViewSetting("detail")}
              maxStandingOrderDate={moment
                .utc()
                .add(2, "years")
                .toDate()}
              handleSetSchedule={form => {
                handleScheduleViewSetting("detail");
                handleSetSchedule(form);
              }}
              isLoading={isLoading}
            />
          ) : (
            <ScheduleDetailForm
              isScheduleLoading={isScheduleLoading}
              schedule={schedule}
              terminology={terminology}
              handleReactivateSchedule={handleReactivateSchedule}
              handleSuspendSchedule={handleSuspendSchedule}
              data={list.data}
              drop={drop}
              setScheduleViewSetting={handleScheduleViewSetting}
              scheduleErrors={scheduleErrors}
              orderStatuses={orderStatuses}
              isLoading={isLoading}
            />
          )}
        </PopOutContainer>
      );
    }
  }

  return (
    <Container>
      {scheduleForm}
    </Container>
  );
};

const Container = styled.div`
  margin-top: ${({ theme }) => theme.spacing.margins.xs};
  display: flex;
  border-radius: ${({ theme }) => theme.borderRadius};
  background: white;
  padding: ${({ theme }) => theme.spacing.padding.md};
  width: 100%;
`;

const PopOutContainer = styled.div`
  ${({ theme }) => css`
    color: ${theme.colors.lightestGrey};
    width: 100%;
    text-align: left;
  `};
`;

const ScheduleStatus = styled(Text)`
  ${({ theme }) => css`
    font-weight: bold;
    display: flex;

    > svg {
      margin-right: ${theme.spacing.padding.xs};
    }

    &.success {
      color: ${theme.colors.success};
    }

    &.warning {
      color: ${theme.colors.warn};
    }

    &.update {
      color: ${theme.colors.primaryDark};
    }

    &.error {
      color: ${theme.colors.error};
    }
  `}
`;

export default ListScheduleInfo;
